/**
 * Verifica se um elemento HTML possui rolagem.
 *
 * @param target Elemento HTML a ser verificado.
 *
 * @category Scroll
 */
export const isElementScrollable = (target: HTMLElement | Document | DocumentFragment): boolean => {
  const element = (target instanceof Document ? document.scrollingElement : target) as HTMLElement;

  // um DocumentFragment é usado antes de renderizar em tela, então não possui rolagem
  if (!element || element instanceof DocumentFragment) {
    return false;
  }

  const { overflowX, overflowY } = getComputedStyle(element);

  return (
    ((overflowX === 'auto' || overflowX === 'scroll') &&
      element.scrollWidth > element.offsetWidth) ||
    ((overflowY === 'auto' || overflowY === 'scroll') &&
      element.scrollHeight > element.offsetHeight)
  );
};

/**
 * Realiza scroll horizontal centralizando o elemento dentro de um container com rolagem.
 *
 * @param target Elemento HTML a ser centralizado.
 * @param container Elemento HTML com rolagem onde o elemento a ser centralizado está contido.
 * @param scrollBehavior Comportamento do scroll a ser executado.
 *
 * @category Scroll
 */
export const scrollIntoViewX = (
  target: HTMLElement,
  container: HTMLElement,
  scrollBehavior: 'auto' | 'smooth' = 'smooth',
) => {
  const scroll = target.offsetLeft - (container.clientWidth - target.offsetWidth) / 2;

  if (container && container.scrollTo) {
    container.scrollTo({
      left: scroll,
      behavior: scrollBehavior,
    });
  }
};

/**
 * Procura pelo primeiro elemento HTML pai com rolagem.
 *
 * @param target Elemento HTML onde começará a busca.
 *
 * @category Scroll
 */
export const getScrollingParent = (target?: HTMLElement | DocumentFragment): HTMLElement => {
  // caso chegue no elemento raiz da aplicação ou em um DocumentFragment
  // retorna o elemento de rolagem padrão do documento
  if (!target || target instanceof DocumentFragment) {
    return document.scrollingElement as HTMLElement;
  }

  if (isElementScrollable(target)) {
    return target;
  }

  return getScrollingParent(target.parentNode as HTMLElement);
};

/**
 * Verifica se o último elemento não está parcialmente visível dentro de um container com rolagem e o exibe.
 *
 * @param target Elemento HTML com rolagem contento os elementos a serem verificados.
 * @param itemWidth Largura de cada elemento do conteúdo
 * @param scrollOffset Valor de deslocamento do scroll.
 */
export const scrollToVisibility = (target: HTMLElement, itemWidth = 0, scrollOffset = 40) => {
  const targetWidth = target.offsetWidth;
  const remainder = targetWidth % itemWidth;
  const mininumRemainderThreshold = scrollOffset;
  const maximumRemainderThreshold = itemWidth - scrollOffset / 2;

  const scrollValue =
    remainder > maximumRemainderThreshold
      ? itemWidth - remainder + scrollOffset
      : remainder < mininumRemainderThreshold
      ? scrollOffset - remainder
      : 0;

  if (scrollValue > 0) {
    target.scrollLeft = scrollValue;
  }
};
